import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDropdown, type BaseDropdownOption } from './BaseDropdown';

export type PostStatus =
  | 'all'
  | 'idea'
  | 'draft'
  | 'scheduled'
  | 'pending'
  | 'published'
  | 'waiting_for_approval'
  | 'rejected'
  | 'error'
  | 'processing'
  | 'ready_to_publish';

interface PostStatusDropdownProps {
  value: PostStatus;
  onChange: (value: PostStatus) => void;
  className?: string;
}

interface StatusDefinition extends BaseDropdownOption {
  id: PostStatus;
}

export const PostStatusDropdown: React.FC<PostStatusDropdownProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const { t, i18n } = useTranslation();

  // Memoize statuses so they update on language change
  const statuses: Array<StatusDefinition> = useMemo(() => [
    { id: 'all', label: t('statusDropdown.all', 'All Statuses'), emoji: '📊', description: t('statusDropdown.allDesc', 'View posts across all their current states.') },
    { id: 'idea', label: t('statusDropdown.idea', 'Idea'), emoji: '💡', description: t('statusDropdown.ideaDesc', 'A post idea saved for later with an optional reminder date.') },
    { id: 'draft', label: t('statusDropdown.draft', 'Draft'), emoji: '📝', description: t('statusDropdown.draftDesc', 'Post is in progress, not yet submitted for review or scheduling.') },
    { id: 'scheduled', label: t('statusDropdown.scheduled', 'Scheduled'), emoji: '📆', description: t('statusDropdown.scheduledDesc', 'Post is approved and set for automatic publishing at a future time.') },
    { id: 'ready_to_publish', label: t('statusDropdown.ready_to_publish', 'Ready to Publish'), emoji: '🚀', description: t('statusDropdown.ready_to_publishDesc', 'Post is complete, approved, and awaiting its scheduled time or manual publishing.') },
    { id: 'waiting_for_approval', label: t('statusDropdown.waiting_for_approval', 'Waiting for approval'), emoji: '⏳', description: t('statusDropdown.waiting_for_approvalDesc', 'Post has been submitted and is awaiting review by an approver.') },
    { id: 'published', label: t('statusDropdown.published', 'Published'), emoji: '✅', description: t('statusDropdown.publishedDesc', 'Post has been successfully shared on the selected platforms.') },
    { id: 'rejected', label: t('statusDropdown.rejected', 'Rejected'), emoji: '❌', description: t('statusDropdown.rejectedDesc', 'Post was reviewed but not approved for publishing by an approver.') },
    { id: 'error', label: t('statusDropdown.error', 'Error'), emoji: '⚠️', description: t('statusDropdown.errorDesc', 'An error occurred while trying to publish or process the post.') },
    { id: 'processing', label: t('statusDropdown.processing', 'Processing'), emoji: '⚙️', description: t('statusDropdown.processingDesc', 'Post is currently being processed by the system (e.g., media upload, API submission).') },
    { id: 'pending', label: t('statusDropdown.pending', 'Pending'), emoji: '🚧', description: t('statusDropdown.pendingDesc', 'Post is awaiting system action or external confirmation before proceeding.') },
  ], [t, i18n.language]);

  return (
    <BaseDropdown
      value={value}
      onChange={(newValue) => onChange(newValue as PostStatus)}
      options={statuses}
      className={className}
    />
  );
};